/**
 * Step 5 — preview & export. Generic read-only table driven by the enabled
 * columns; a registry entry may provide a custom PreviewComponent instead
 * (and future editable report types will hook in here).
 */

import { FilePdf, WarningCircle, CalendarX } from "@phosphor-icons/react";
import ContentLoader from "../../../../shared/ContentLoader";
import LoaderLoop from "../../../../shared/loader/LoaderLoop";
import { formatHebrewDate } from "../../../../../utils/dateUtils";

function PreviewStep({
  wiz,
  columns, // enabled columns [{key, label}]
  rows, // built row objects
  isLoadingData,
  dataError,
  canExportData,
  isExporting,
  exportError,
  onExport,
  patientName,
}) {
  const { startDate, endDate, reportType } = wiz;

  if (isLoadingData) {
    return (
      <div className="prw-step-panel">
        <ContentLoader message="טוען נתונים..." />
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="prw-step-panel">
        <div className="prw-preview-state error">
          <WarningCircle size={22} />
          <span>אירעה שגיאה בטעינת הנתונים. נסו שוב בעוד רגע.</span>
        </div>
      </div>
    );
  }

  const CustomPreview = reportType?.PreviewComponent;

  return (
    <div className="prw-step-panel">
      <div className="prw-preview-header">
        <div className="prw-preview-title">
          <span className="prw-preview-name">{reportType?.label}</span>
          <span className="prw-preview-meta">
            {patientName} · {formatHebrewDate(startDate)} –{" "}
            {formatHebrewDate(endDate)} · {rows.length} פגישות
          </span>
        </div>
        {canExportData && rows.length > 0 && (
          <button
            type="button"
            className="prw-btn primary prw-export-btn"
            onClick={onExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <LoaderLoop size="xs" />
            ) : (
              <FilePdf size={16} weight="fill" />
            )}
            {isExporting ? "מפיק דוח..." : "ייצוא PDF"}
          </button>
        )}
      </div>

      {exportError && <div className="prw-field-error">{exportError}</div>}

      {rows.length === 0 ? (
        <div className="prw-preview-state">
          <CalendarX size={22} />
          <span>אין פגישות בטווח שנבחר</span>
        </div>
      ) : CustomPreview ? (
        <CustomPreview columns={columns} rows={rows} />
      ) : (
        <div className="prw-table-wrap">
          <table className="prw-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.key}>{row[c.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PreviewStep;
